/**
 * DialerStatsTab.jsx - Dialer-specific statistics tab for Monitoring page
 * Charts: Donut (dispositions), Bar (per-bot breakdown), Line (voice set comparison)
 * Universal disposition colors matching CallLogsTab/MonitoringV3
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
    Phone, PhoneForwarded, PhoneOff, Bot, Clock, Ban,
    RefreshCw, Loader2
} from 'lucide-react'
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
    LineChart, Line
} from 'recharts'
import { monitoringApi } from '../services/api'
import './DialerStatsTab.css'

// ============================================
// Universal disposition colors (matching CallLogsTab)
// ============================================
const DISP_COLORS = {
    transfer: '#10b981',    // Emerald - Transfers
    am: '#8b5cf6',          // Violet  - Answering Machine
    ni: '#f59e0b',          // Amber   - Not Interested
    dnc: '#ef4444',         // Red     - Do Not Call
    noAnswer: '#6b7280',    // Gray    - No Answer
    callback: '#3b82f6',    // Blue    - Callback
    calls: '#3b82f6',       // Blue    - Total calls (bar chart)
}

// Donut chart uses the same order/colors
const PIE_COLOR_MAP = {
    'no answer': '#6b7280',
    'no_answer': '#6b7280',
    'no-answer': '#6b7280',
    'not interested': '#f59e0b',
    'not_interested': '#f59e0b',
    'not-interested': '#f59e0b',
    'ans. machine': '#8b5cf6',
    'answering machine': '#8b5cf6',
    'answering-machine': '#8b5cf6',
    'answering_machine': '#8b5cf6',
    'transfer': '#10b981',
    'transfer-call': '#10b981',
    'call-transfered': '#10b981',
    'transferred': '#10b981',
    'do not call': '#ef4444',
    'do-not-call': '#ef4444',
    'do_not_call': '#ef4444',
    'dnc': '#ef4444',
    'call back': '#3b82f6',
    'call-back': '#3b82f6',
    'call_back': '#3b82f6',
    'callback': '#3b82f6',
    'busy': '#f97316',
    'customer-busy': '#f97316',
    'customer_busy': '#f97316',
}
const PIE_FALLBACK_COLORS = ['#6366f1', '#ec4899', '#14b8a6', '#a855f7', '#06b6d4', '#84cc16']

const LINE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']

const DISPOSITION_LABELS = {
    'not-interested': 'Not Interested', 'not_interested': 'Not Interested',
    'answering-machine': 'Ans. Machine', 'answering_machine': 'Ans. Machine',
    'do-not-call': 'Do Not Call', 'do_not_call': 'Do Not Call',
    'no-answer': 'No Answer', 'no_answer': 'No Answer',
    'call-back': 'Call Back', 'call_back': 'Call Back',
    'transfer-call': 'Transfer', 'call-transfered': 'Transferred', 'TRANSFER': 'Transfer',
    'age-do-not-qualify': 'Age DNQ', 'age_do_not_qualify': 'Age DNQ',
}

const formatLabel = (str) => {
    if (!str) return 'Unknown'
    if (DISPOSITION_LABELS[str]) return DISPOSITION_LABELS[str]
    return str.replace(/[-_]/g, ' ').split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
}

function getPieColor(disposition, index) {
    const key = (disposition || '').toLowerCase()
    if (PIE_COLOR_MAP[key]) return PIE_COLOR_MAP[key]
    const label = formatLabel(disposition).toLowerCase()
    if (PIE_COLOR_MAP[label]) return PIE_COLOR_MAP[label]
    return PIE_FALLBACK_COLORS[index % PIE_FALLBACK_COLORS.length]
}

const formatDuration = (seconds) => {
    if (!seconds) return '0s'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins > 0) return `${mins}m ${secs}s`
    return `${secs}s`
}

// Time interval definitions
const TIME_INTERVALS = [
    { key: '5m', label: '5m', minutes: 5 },
    { key: '10m', label: '10m', minutes: 10 },
    { key: '30m', label: '30m', minutes: 30 },
    { key: '1h', label: '1h', minutes: 60 },
    { key: '4h', label: '4h', minutes: 240 },
    { key: '12h', label: '12h', minutes: 720 },
    { key: 'today', label: 'Today', minutes: null },
    { key: 'yesterday', label: 'Yesterday', minutes: null },
    { key: '7d', label: '7D', minutes: null },
    { key: '30d', label: '30D', minutes: null },
    { key: 'range', label: 'Range', minutes: null },
]

function getDateRange(intervalKey) {
    const now = new Date()
    const interval = TIME_INTERVALS.find(t => t.key === intervalKey)
    if (interval?.minutes) {
        const from = new Date(now.getTime() - interval.minutes * 60 * 1000)
        return { from: from.toISOString().slice(0, 19), to: now.toISOString().slice(0, 19), isSubDay: true }
    }
    const todayStr = now.toISOString().split('T')[0]
    switch (intervalKey) {
        case 'today': return { from: todayStr, to: todayStr, isSubDay: false }
        case 'yesterday': { const y = new Date(now); y.setDate(y.getDate() - 1); return { from: y.toISOString().split('T')[0], to: y.toISOString().split('T')[0], isSubDay: false } }
        case '7d': { const d = new Date(now); d.setDate(d.getDate() - 7); return { from: d.toISOString().split('T')[0], to: todayStr, isSubDay: false } }
        case '30d': { const d = new Date(now); d.setDate(d.getDate() - 30); return { from: d.toISOString().split('T')[0], to: todayStr, isSubDay: false } }
        default: return { from: todayStr, to: todayStr, isSubDay: false }
    }
}

function getMinDate() {
    const d = new Date()
    d.setMonth(d.getMonth() - 3)
    return d.toISOString().split('T')[0]
}

// Custom tooltips
function DonutTooltip({ active, payload }) {
    if (active && payload && payload.length) {
        const data = payload[0].payload
        return (
            <div className="dstats-tooltip">
                <p className="dstats-tooltip-label">{data.name}</p>
                <p className="dstats-tooltip-value">{data.value.toLocaleString()} calls ({data.percentage}%)</p>
            </div>
        )
    }
    return null
}

function BarTooltip({ active, payload, label }) {
    if (active && payload && payload.length) {
        return (
            <div className="dstats-tooltip">
                <p className="dstats-tooltip-label">SIP {label}</p>
                {payload.map((p, i) => (
                    <p key={i} className="dstats-tooltip-value" style={{ color: p.color || p.fill }}>
                        {p.name}: {p.value.toLocaleString()}
                    </p>
                ))}
            </div>
        )
    }
    return null
}

function LineTooltip({ active, payload, label }) {
    if (active && payload && payload.length) {
        return (
            <div className="dstats-tooltip">
                <p className="dstats-tooltip-label">{label}</p>
                {payload.map((p, i) => (
                    <p key={i} className="dstats-tooltip-value" style={{ color: p.color }}>
                        {p.name}: {p.value}%
                    </p>
                ))}
            </div>
        )
    }
    return null
}

export default function DialerStatsTab({ dialerId, dialerName }) {
    const [timeInterval, setTimeInterval] = useState('today')
    const [customFrom, setCustomFrom] = useState('')
    const [customTo, setCustomTo] = useState('')
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)

    const [overview, setOverview] = useState(null)
    const [dispositions, setDispositions] = useState(null)
    const [botStats, setBotStats] = useState([])
    const [voiceSetStats, setVoiceSetStats] = useState([])

    const autoRefreshRef = useRef(null)

    const fetchStats = useCallback(async (showLoading = false) => {
        if (!dialerId) return
        if (showLoading) setLoading(true)
        else setRefreshing(true)

        try {
            let from, to
            if (timeInterval === 'range') {
                from = customFrom; to = customTo
                if (!from || !to) { setLoading(false); setRefreshing(false); return }
            } else {
                const range = getDateRange(timeInterval)
                from = range.from; to = range.to
            }

            const params = { from, to, dialer_id: dialerId }
            const [overviewRes, dispRes, botRes, vsRes] = await Promise.allSettled([
                monitoringApi.getStatsOverview(params),
                monitoringApi.getDispositionStats(params),
                monitoringApi.getStatsByBot(params),
                monitoringApi.getStatsByVoiceSet(params),
            ])

            if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value.data)
            if (dispRes.status === 'fulfilled') setDispositions(dispRes.value.data)
            if (botRes.status === 'fulfilled') setBotStats(botRes.value.data?.bots || [])
            if (vsRes.status === 'fulfilled') setVoiceSetStats(vsRes.value.data?.voice_sets || [])
        } catch (err) {
            console.error('[DialerStatsTab] fetch error:', err)
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [dialerId, timeInterval, customFrom, customTo])

    useEffect(() => { fetchStats(true) }, [fetchStats])

    useEffect(() => {
        if (autoRefreshRef.current) { clearInterval(autoRefreshRef.current); autoRefreshRef.current = null }
        const intDef = TIME_INTERVALS.find(t => t.key === timeInterval)
        if (intDef?.minutes) {
            autoRefreshRef.current = window.setInterval(() => fetchStats(false), 30000)
        }
        return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current) }
    }, [timeInterval, fetchStats])

    const isSubDay = TIME_INTERVALS.find(t => t.key === timeInterval)?.minutes != null
    const totalCalls = overview?.overview?.total_calls || 0
    const transfers = overview?.overview?.transfers || { count: 0, percentage: 0 }
    const am = overview?.overview?.answering_machines || { count: 0, percentage: 0 }
    const ni = overview?.overview?.not_interested || { count: 0, percentage: 0 }
    const dnc = overview?.overview?.dnc || { count: 0, percentage: 0 }
    const avgDuration = overview?.overview?.avg_duration || 0
    const avgSteps = overview?.overview?.avg_steps || 0

    // Donut chart data with universal colors
    const pieData = (dispositions?.dispositions || []).map((d, i) => ({
        name: formatLabel(d.disposition),
        value: d.count,
        percentage: d.percentage,
        color: getPieColor(d.disposition, i)
    }))

    // Bar chart data - top 15 bots, 6 colored disposition bars
    const barData = botStats.slice(0, 15).map(b => ({
        sip: b.sip_id,
        Transfers: b.transfers,
        AM: b.answering_machines,
        NI: b.not_interested,
        DNC: b.dnc,
        'No Ans': b.total_calls - b.transfers - b.answering_machines - b.not_interested - b.dnc > 0
            ? b.total_calls - b.transfers - b.answering_machines - b.not_interested - b.dnc
            : 0,
    }))

    // Line chart data - top 6 voice sets, forex-style multi-line
    const topVoiceSets = voiceSetStats.slice(0, 6)
    const lineMetrics = ['Xfer%', 'AM%', 'NI%', 'Avg Dur']
    const lineData = lineMetrics.map(metric => {
        const point = { metric }
        topVoiceSets.forEach(vs => {
            const shortName = (vs.voice_set_name || '').replace(' VoiceSet', '').replace(' VocieSet', '').substring(0, 10)
            if (metric === 'Xfer%') point[shortName] = vs.transfer_rate
            else if (metric === 'AM%') {
                point[shortName] = vs.total_calls > 0
                    ? parseFloat(((vs.dispositions?.answering_machine || 0) / vs.total_calls * 100).toFixed(1))
                    : 0
            }
            else if (metric === 'NI%') {
                point[shortName] = vs.total_calls > 0
                    ? parseFloat(((vs.dispositions?.not_interested || 0) / vs.total_calls * 100).toFixed(1))
                    : 0
            }
            else if (metric === 'Avg Dur') point[shortName] = vs.avg_duration
        })
        return point
    })

    const vsLineKeys = topVoiceSets.map(vs =>
        (vs.voice_set_name || '').replace(' VoiceSet', '').replace(' VocieSet', '').substring(0, 10)
    )

    if (loading) {
        return (
            <div className="dstats-loading">
                <Loader2 size={18} className="spin" />
                Loading stats...
            </div>
        )
    }

    return (
        <div className="dstats-container">
            {/* Toolbar - right aligned with integrated refresh */}
            <div className="dstats-toolbar">
                {isSubDay && (
                    <span className="dstats-auto-badge">
                        <span className="dstats-auto-dot" />
                        Auto 30s
                    </span>
                )}
                <div className="dstats-toolbar-controls">
                    {timeInterval === 'range' && (
                        <div className="dstats-range-inputs">
                            <input type="date" value={customFrom} min={getMinDate()} max={customTo || new Date().toISOString().split('T')[0]} onChange={e => setCustomFrom(e.target.value)} />
                            <span>-</span>
                            <input type="date" value={customTo} min={customFrom || getMinDate()} max={new Date().toISOString().split('T')[0]} onChange={e => setCustomTo(e.target.value)} />
                        </div>
                    )}
                    <div className="dstats-time-pills">
                        {TIME_INTERVALS.map(t => (
                            <button
                                key={t.key}
                                className={`dstats-time-pill ${timeInterval === t.key ? 'active' : ''}`}
                                onClick={() => setTimeInterval(t.key)}
                            >
                                {t.label}
                            </button>
                        ))}
                        <button
                            className="dstats-time-pill dstats-refresh-pill"
                            onClick={() => fetchStats(false)}
                            disabled={refreshing}
                            title="Refresh"
                        >
                            <RefreshCw size={12} className={refreshing ? 'spin' : ''} />
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI stat blocks */}
            <div className="stat-blocks">
                <div className="stat-block blue">
                    <div className="stat-block-icon"><Phone size={18} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{totalCalls.toLocaleString()}</span>
                        <span className="stat-block-label">Total Calls</span>
                    </div>
                </div>
                <div className="stat-block green">
                    <div className="stat-block-icon"><PhoneForwarded size={18} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{transfers.count.toLocaleString()}</span>
                        <span className="stat-block-label">Transfers ({transfers.percentage}%)</span>
                    </div>
                </div>
                <div className="stat-block purple">
                    <div className="stat-block-icon"><Bot size={18} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{am.count.toLocaleString()}</span>
                        <span className="stat-block-label">AM ({am.percentage}%)</span>
                    </div>
                </div>
                <div className="stat-block orange">
                    <div className="stat-block-icon"><PhoneOff size={18} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{ni.count.toLocaleString()}</span>
                        <span className="stat-block-label">Not Int. ({ni.percentage}%)</span>
                    </div>
                </div>
                <div className="stat-block red">
                    <div className="stat-block-icon"><Ban size={18} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{dnc.count.toLocaleString()}</span>
                        <span className="stat-block-label">DNC ({dnc.percentage}%)</span>
                    </div>
                </div>
                <div className="stat-block cyan">
                    <div className="stat-block-icon"><Clock size={18} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{formatDuration(avgDuration)}</span>
                        <span className="stat-block-label">Avg Duration ({avgSteps} steps)</span>
                    </div>
                </div>
            </div>

            {/* Row 1: Donut + Voice Set Line Chart */}
            <div className="dstats-charts-row">
                {/* Disposition Donut */}
                <div className="dstats-chart-card">
                    <h3 className="dstats-chart-title">Disposition Breakdown</h3>
                    {pieData.length > 0 ? (
                        <div className="dstats-chart-with-legend">
                            <div className="dstats-pie-container">
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value">
                                            {pieData.map((entry, index) => (
                                                <Cell key={index} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<DonutTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="dstats-legend">
                                {pieData.slice(0, 7).map((item, i) => (
                                    <div key={i} className="dstats-legend-item">
                                        <span className="dstats-legend-color" style={{ background: item.color }} />
                                        <span className="dstats-legend-label">{item.name}</span>
                                        <span className="dstats-legend-value">{item.percentage}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="dstats-empty">No disposition data</div>
                    )}
                </div>

                {/* Voice Set Line Chart (forex style) */}
                <div className="dstats-chart-card">
                    <h3 className="dstats-chart-title">
                        Voice Set Comparison
                        {topVoiceSets.length > 0 && <span className="dstats-chart-subtitle"> (Top {topVoiceSets.length})</span>}
                    </h3>
                    {topVoiceSets.length > 1 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={lineData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="metric" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} width={35} />
                                <Tooltip content={<LineTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="circle" iconSize={8} />
                                {vsLineKeys.map((key, i) => (
                                    <Line key={key} type="monotone" dataKey={key} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 4, fill: LINE_COLORS[i % LINE_COLORS.length] }} activeDot={{ r: 6 }} />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    ) : topVoiceSets.length === 1 ? (
                        <div className="dstats-single-vs">
                            <strong>{topVoiceSets[0].voice_set_name}</strong>
                            <span>{topVoiceSets[0].total_calls.toLocaleString()} calls</span>
                            <span style={{ color: DISP_COLORS.transfer }}>{topVoiceSets[0].transfer_rate}% xfer</span>
                            <span>{formatDuration(topVoiceSets[0].avg_duration)} avg</span>
                        </div>
                    ) : (
                        <div className="dstats-empty">No voice set data</div>
                    )}
                </div>
            </div>

            {/* Row 2: Bot Performance Stacked Bar Chart - disposition breakdown per bot */}
            {barData.length > 0 && (
                <div className="dstats-chart-card dstats-full-width">
                    <h3 className="dstats-chart-title">
                        Bot Performance
                        <span className="dstats-chart-subtitle"> (Top {barData.length} by calls)</span>
                    </h3>
                    <ResponsiveContainer width="100%" height={340}>
                        <BarChart data={barData} margin={{ top: 10, right: 16, left: 0, bottom: 30 }} barGap={2} barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis
                                dataKey="sip"
                                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                angle={-40}
                                textAnchor="end"
                                height={55}
                            />
                            <YAxis
                                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                width={45}
                            />
                            <Tooltip content={<BarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)', radius: 4 }} />
                            <Legend
                                wrapperStyle={{ fontSize: 12, paddingTop: 12, paddingBottom: 0 }}
                                iconType="circle"
                                iconSize={8}
                            />
                            <Bar dataKey="Transfers" stackId="a" fill={DISP_COLORS.transfer} />
                            <Bar dataKey="AM" stackId="a" fill={DISP_COLORS.am} />
                            <Bar dataKey="NI" stackId="a" fill={DISP_COLORS.ni} />
                            <Bar dataKey="DNC" stackId="a" fill={DISP_COLORS.dnc} />
                            <Bar dataKey="No Ans" stackId="a" fill={DISP_COLORS.noAnswer} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Row 3: Per-bot table */}
            {botStats.length > 0 && (
                <div className="dstats-table-card">
                    <div className="dstats-table-header">
                        <span className="dstats-table-title">All Bots</span>
                        <span className="dstats-table-count">{botStats.length} bots</span>
                    </div>
                    <div className="dstats-table-scroll">
                        <table className="dstats-table">
                            <thead>
                                <tr>
                                    <th>SIP ID</th>
                                    <th className="text-right">Calls</th>
                                    <th className="text-right">Xfer</th>
                                    <th className="text-right">Xfer%</th>
                                    <th className="text-right">AM</th>
                                    <th className="text-right">NI</th>
                                    <th className="text-right">DNC</th>
                                    <th className="text-right">Avg Dur</th>
                                    <th className="text-right">Steps</th>
                                </tr>
                            </thead>
                            <tbody>
                                {botStats.map(bot => (
                                    <tr key={bot.sip_id}>
                                        <td>{bot.sip_id}</td>
                                        <td className="text-right">{bot.total_calls.toLocaleString()}</td>
                                        <td className="text-right" style={{ color: DISP_COLORS.transfer }}>{bot.transfers}</td>
                                        <td className="text-right" style={{ color: DISP_COLORS.transfer, fontWeight: 600 }}>{bot.transfer_rate}%</td>
                                        <td className="text-right" style={{ color: DISP_COLORS.am }}>{bot.answering_machines}</td>
                                        <td className="text-right" style={{ color: DISP_COLORS.ni }}>{bot.not_interested}</td>
                                        <td className="text-right" style={{ color: DISP_COLORS.dnc }}>{bot.dnc}</td>
                                        <td className="text-right">{formatDuration(bot.avg_duration)}</td>
                                        <td className="text-right">{bot.avg_steps}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}
